import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Instagram, Facebook, Mail } from 'lucide-react';
import WelcomePopup from '@/components/WelcomePopup';

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.49a8.28 8.28 0 0 0 4.76 1.51v-3.4a4.85 4.85 0 0 1-1-.09z"/>
  </svg>
);

const PinterestIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' },
  { path: '/gratitude-journal', label: 'Books & Projects' },
  { path: 'https://cuavena.myshopify.com', label: 'Shopify', external: true },
  { path: '/spread-the-word', label: 'Spread the Word' },
  { path: '/contact', label: 'Contact' },
];

const policyLinks = [
  { path: '/privacy-policy', label: 'Privacy Policy' },
  { path: '/terms-of-service', label: 'Terms of Service' },
  { path: '/shipping-policy', label: 'Shipping Policy' },
  { path: '/return-policy', label: 'Return & Refund Policy' },
];

function NavItem({ link, location, className }) {
  if (link.external) {
    return (
      <a
        href={link.path}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="nav-link-shop"
        className={className || "text-sm font-medium tracking-wide transition-colors duration-200 text-[#2A2421]/70 hover:text-[#A67A5B]"}
      >
        {link.label}
      </a>
    );
  }
  return (
    <Link
      to={link.path}
      data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
      className={className || `text-sm font-medium tracking-wide transition-colors duration-200 ${
        location.pathname === link.path
          ? 'text-[#A67A5B]'
          : 'text-[#2A2421]/70 hover:text-[#A67A5B]'
      }`}
    >
      {link.label}
    </Link>
  );
}

export default function Layout({ children }) {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          scrolled
            ? 'backdrop-blur-xl bg-[#FDFBF7]/90 shadow-sm border-b border-[#E8E1D9]/50'
            : 'bg-[#FDFBF7] border-b border-[#E8E1D9]/30'
        }`}
      >
        {/* Top row: Logo */}
        <div className="max-w-7xl mx-auto px-5 md:px-12 flex items-center justify-between h-14 md:h-16">
          <Link
            to="/"
            data-testid="brand-logo"
            className="font-serif text-lg md:text-2xl font-semibold tracking-wide text-[#2A2421]"
          >
            Led By Faith Studios
          </Link>

          {/* Desktop nav - inline with logo */}
          <nav className="hidden md:flex items-center gap-7" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <NavItem key={link.label} link={link} location={location} />
            ))}
          </nav>
        </div>

        {/* Mobile nav - visible wrapping tab row */}
        <nav
          className="md:hidden border-t border-[#E8E1D9]/40"
          data-testid="mobile-nav"
        >
          <div className="flex flex-wrap items-center justify-center gap-1.5 px-3 py-2.5">
            {navLinks.map((link) => {
              const isActive = !link.external && location.pathname === link.path;
              if (link.external) {
                return (
                  <a
                    key={link.label}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-nav-shop"
                    className="px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all duration-200 bg-transparent text-[#2A2421]/60 hover:text-[#A67A5B] hover:bg-[#A67A5B]/5"
                  >
                    {link.label}
                  </a>
                );
              }
              return (
                <Link
                  key={link.label}
                  to={link.path}
                  data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all duration-200 ${
                    isActive
                      ? 'bg-[#A67A5B] text-white'
                      : 'bg-transparent text-[#2A2421]/60 hover:text-[#A67A5B] hover:bg-[#A67A5B]/5'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>

      {/* Main content */}
      <main className="flex-1">{children}</main>

      {/* Welcome Popup */}
      <WelcomePopup />

      {/* Footer */}
      <footer className="bg-[#2A2421] text-white" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-18">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-10">
            {/* Brand */}
            <div>
              <h3 className="font-serif text-xl font-semibold mb-2 text-white">
                Led By Faith Studios
              </h3>
              <p className="text-xs text-[#C59B76]/60 mb-3 font-sans">Published by Cypress Admin Studio</p>
              <p className="text-white/50 text-sm leading-relaxed max-w-xs">
                Faith-centered books, journals, and products rooted in resilience, gratitude, and encouragement.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-sans font-semibold text-xs tracking-[0.2em] uppercase mb-5 text-[#C59B76]">
                Quick Links
              </h4>
              <nav className="flex flex-col gap-2.5">
                {navLinks.map((link) => (
                  link.external ? (
                    <a key={link.label} href={link.path} target="_blank" rel="noopener noreferrer" data-testid="footer-link-shop" className="text-sm text-white/50 hover:text-[#C59B76] transition-colors duration-200">{link.label}</a>
                  ) : (
                    <Link key={link.label} to={link.path} data-testid={`footer-link-${link.path.replace('/', '') || 'home'}`} className="text-sm text-white/50 hover:text-[#C59B76] transition-colors duration-200">{link.label}</Link>
                  )
                ))}
              </nav>
            </div>

            {/* Policies */}
            <div>
              <h4 className="font-sans font-semibold text-xs tracking-[0.2em] uppercase mb-5 text-[#C59B76]">
                Policies
              </h4>
              <nav className="flex flex-col gap-2.5">
                {policyLinks.map((link) => (
                  <Link key={link.path} to={link.path} data-testid={`footer-${link.path.slice(1)}`} className="text-sm text-white/50 hover:text-[#C59B76] transition-colors duration-200">{link.label}</Link>
                ))}
              </nav>
            </div>

            {/* Connect */}
            <div>
              <h4 className="font-sans font-semibold text-xs tracking-[0.2em] uppercase mb-5 text-[#C59B76]">
                Connect
              </h4>
              <div className="flex gap-2.5 mb-5 flex-wrap">
                {[
                  { href: 'https://instagram.com/ledbyfaithstudios', label: 'Instagram', icon: <Instagram size={16} />, testId: 'footer-instagram' },
                  { href: 'https://facebook.com/ledbyfaithstudios', label: 'Facebook', icon: <Facebook size={16} />, testId: 'footer-facebook' },
                  { href: 'https://tiktok.com/@ledbyfaithstudios', label: 'TikTok', icon: <TikTokIcon size={14} />, testId: 'footer-tiktok' },
                  { href: 'https://pinterest.com/ledbyfaithstudios', label: 'Pinterest', icon: <PinterestIcon size={14} />, testId: 'footer-pinterest' },
                  { href: '/contact', label: 'Contact', icon: <Mail size={16} />, testId: 'footer-contact-link', internal: true },
                ].map((s) => (
                  s.internal ? (
                    <Link
                      key={s.testId}
                      to={s.href}
                      data-testid={s.testId}
                      className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/35 hover:text-[#C59B76] hover:border-[#C59B76] transition-all duration-300"
                      aria-label={s.label}
                    >
                      {s.icon}
                    </Link>
                  ) : (
                    <a
                      key={s.testId}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={s.testId}
                      className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/35 hover:text-[#C59B76] hover:border-[#C59B76] transition-all duration-300"
                      aria-label={s.label}
                    >
                      {s.icon}
                    </a>
                  )
                ))}
              </div>
              <Link
                to="/contact"
                data-testid="footer-contact-text-link"
                className="text-xs text-[#C59B76]/80 hover:text-[#C59B76] underline underline-offset-2 transition-colors"
              >
                Contact us here
              </Link>
              {/* QR Code */}
              <div className="mt-5 flex items-center gap-3">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent('https://ledbyfaithstudios.com')}&color=C59B76&bgcolor=2A2421&margin=1`}
                  alt="QR Code"
                  data-testid="footer-qr-code"
                  className="w-14 h-14 rounded-md"
                />
                <p className="text-[10px] text-white/25 leading-tight">Scan to visit<br />or share this site</p>
              </div>
            </div>
          </div>

          <div className="border-t border-white/8 mt-10 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-[10px] text-white/25">
              &copy; {new Date().getFullYear()} Led By Faith Studios. All rights reserved.
            </p>
            <div className="flex items-center gap-3">
              <Link to="/privacy-policy" className="text-[10px] text-white/20 hover:text-white/40 transition-colors">Privacy</Link>
              <Link to="/terms-of-service" className="text-[10px] text-white/20 hover:text-white/40 transition-colors">Terms</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
